<template>
  <div
    class="shop-the-look critical-component-clear"
    :class="[
      {
        'shop-the-look--reverse': reverse,
        'shop-the-look--single': hasSingleSlide,
      },
      theme
    ]"
  >
    <div class="shop-the-look__container container">
      <Teleport
        v-if="isMounted"
        :to="$refs.main"
        :disabled="isMobile"
      >
        <div class="shop-the-look__top">
          <div class="shop-the-look__header">
            <h2
              v-if="title"
              :id="`section-title-${liquid.sectionId}`"
              class="
                shop-the-look__title
                text-heading-serif-m
                text-heading-serif-l-desktop
              "
              :style="{ fontFamily: fontStyle }"
              v-text="title"
            />

            <div
              v-if="subtitle"
              class="
                shop-the-look__subtitle
                text-body-xs
                text-body-s-desktop
              "
              :style="{ fontFamily: fontStyle }"
              v-html="subtitle"
            />
          </div>

          <div
            v-if="!hasSingleSlide"
            class="shop-the-look__arrows splide"
          >
            <div class="splide__arrows">
              <button
                class="splide__arrow splide__arrow--prev"
                :aria-label="$string('accessibility.carousel.previous')"
                @click="slideTo('<')"
              >
                <icon-arrow-left />
              </button>

              <button
                class="splide__arrow"
                :aria-label="$string('accessibility.carousel.next')"
                @click="slideTo('>')"
              >
                <icon-arrow-right />
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <div
        ref="main"
        class="shop-the-look__main"
      >
        <div
          class="shop-the-look__products splide"
          :aria-label="$string('accessibility.shop_the_look.product_carousel')"
          js-carousel="shopTheLookProducts"
        >
          <div class="splide__track">
            <div class="splide__list">
              <div
                v-for="(slide, index) in slides"
                :key="`shop-the-look-products-${index}`"
                class="splide__slide"
              >
                <div class="grid">
                  <template
                    v-if="slide.first_image_card && slide.second_image_card"
                  >
                    <div class="col xs2">
                      <a
                        :href="slide.first_card_link_url"
                        class="shop-the-look__link"
                      >
                        <responsive-image
                          class="shop-the-look__card-image"
                          :image="slide.first_image_card"
                          ratio="3:4"
                        />

                        <div
                          v-if="slide.first_link_text"
                          class="shop-the-look__link-text text-body-3xs"
                          v-html="slide.first_link_text"
                        />
                      </a>
                    </div>

                    <div class="col xs2">
                      <a
                        :href="slide.second_card_link_url"
                        class="shop-the-look__link"
                      >
                        <responsive-image
                          class="shop-the-look__card-image"
                          :image="slide.second_image_card"
                          ratio="3:4"
                        />

                        <div
                          v-if="slide.second_link_text"
                          class="shop-the-look__link-text text-body-3xs"
                          v-html="slide.second_link_text"
                        />
                      </a>
                    </div>
                  </template>

                  <template v-else>
                    <div
                      v-for="product in products[index] ?? slide.products"
                      :key="product.handle"
                      class="col xs2"
                    >
                      <product-card
                        :product="products[index] && product"
                        :image-order="slide.productCardImageOrder"
                        :image-use-alt="slide.productCardUseAltImage"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="shop-the-look__images splide"
        js-carousel="shopTheLookImages"
      >
        <div class="splide__track">
          <div class="splide__list">
            <div
              v-for="(slide, index) in slides"
              :key="`shop-the-look-images-${index}`"
              class="splide__slide"
            >
              <a
                v-if="slide.on_image_link_url"
                :href="slide.on_image_link_url"
              >
                <responsive-image
                  :image="slide.image"
                  ratio="3:4"
                />
              </a>

              <responsive-image
                v-else
                :image="slide.image"
                ratio="3:4"
              />

              <div
                v-if="slide.image_first_link_url || slide.image_second_url"
                class="shop-the-look__image-footer"
              >
                <span
                  class="text-body-3xs"
                  v-text="$string('general.products_images_collage.image_links_prefix')"
                />

                <btn
                  class="shop-the-look__button products-images-collage__imageLink"
                  :label="slide.image_first_text"
                  :url="slide.image_first_link_url"
                  :show-icon="false"
                  modifiers="text"
                />

                <template v-if="slide.image_second_link_url">
                  <span>/</span>
                  <btn
                    class="shop-the-look__button products-images-collage__imageLink"
                    :label="slide.image_second_text"
                    :url="slide.image_second_link_url"
                    :show-icon="false"
                    modifiers="text"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Shop the look (shop-the-look)
 * -----------------------------------------------------------------------------
 * A selection of products with supporting imagery.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} title - The section title.
 * @param {String} subtitle - The section subtitle.
 * @param {Array} slides - A list of slides, containing products and an image.
 * @param {Boolean} reverse - Flips the layout of the carousels.
 * @param {String} fontStyle - Font style for title and subtitle.
 *
 */
import { mapState } from 'vuex'

import Carousel from '~/api/carousel'
import ProductCard from '~async/product-card/product-card'
import ResponsiveImage from '~global/images/responsive-image'

import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconArrowLeft from '~icons/direction/arrow/left.svg'

import product from '~/graphql/queries/product.gql'

import { formatProduct } from '~/helpers/format'
import Btn from '~global/btn/btn.vue'

export default {
  name: 'ShopTheLook',

  components: {
    Btn,
    ProductCard,
    IconArrowRight,
    IconArrowLeft,
    ResponsiveImage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    slides: {
      type: Array,
      default: () => [],
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: '',
    },
    fontStyle: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isMounted: false,
      products: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns if there's only the one slide.
     * @returns {Boolean} - The single slide state.
     */
    hasSingleSlide() {
      return this.slides.length <= 1
    },
  },

  mounted() {
    this.isMounted = true

    this.queryProducts()
    this.constructCarousels()
  },

  methods: {

    /**
     * Synchronously fetches products for each slide.
     * - Maps into an object using the slide index to render into the template.
     * - Product Cards are provided `false` if the queried products don't exist.
     */
    queryProducts() {
      this.slides.forEach(async ({ products }, index) => {
        const response = await Promise.allSettled(
          products.map(({ handle }) =>
            cnvs.Query({
              query: product,
              variables: {
                country: cnvs.store.country,
                handle,
                language: cnvs.store.language,
              },
            }),
          ),
        )

        this.products[index] = response
          .map(({ value }) => value.product ? formatProduct(value) : false)
      })
    },

    /**
     * Constructs the carousel instances.
     */
    constructCarousels() {
      if (this.hasSingleSlide) {
        return
      }

      this.productCarousel = Carousel(
        '[js-carousel="shopTheLookProducts"]',
        {
          arrows: false,
          type: 'fade',
          rewind: true,
        },
      )

      this.imageCarousel = Carousel(
        '[js-carousel="shopTheLookImages"]',
      )

      this.productCarousel.sync(this.imageCarousel)
      this.productCarousel.mount()
      this.imageCarousel.mount()
    },

    /**
     * Slides to the given index.
     * @see https://splidejs.com/guides/apis/#go
     * @param {Number|String} index - The position to slide to.
     */
    slideTo(index) {
      if (!this.productCarousel) {
        return
      }

      this.productCarousel.go(index)
    },
  },
}
</script>

<style lang="scss">
@import './shop-the-look';
</style>
